import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useClaimDevice, useOrganisations } from "../../../../hooks/queries";
import { UtilityService } from "../../../../services/UtilityService";

/**
 * Hook for managing device claiming functionality
 * @param {Function} onClaimSuccess - Callback when device is successfully claimed
 * @returns {Object} Device claiming state and handlers
 */
export const useDeviceClaim = (onClaimSuccess) => {
  const { t } = useTranslation("common");

  // TanStack Query data and mutations
  const { data: organisations = [] } = useOrganisations();
  const claimDeviceMutation = useClaimDevice();

  // Form state - initialize with first org if available
  const [claimCode, setClaimCode] = useState("");
  const [organisationID, setOrganisationID] = useState(
    () => organisations?.[0]?.id
  );

  // Ensure organisationID is set when user selects (controlled by form)
  // The default is set on first render if organisations are already loaded

  // Reset form state
  const resetForm = () => {
    setClaimCode("");
    if (organisations && organisations.length) {
      setOrganisationID(organisations[0].id);
    }
  };

  // Handle device claiming
  const claimDevice = async () => {
    claimDeviceMutation.mutate(
      {
        claimCode: claimCode,
        organisationId: organisationID,
      },
      {
        onSuccess: () => {
          UtilityService.updateSnackbarMessage(
            t("common.device_claimed_successfully")
          );

          if (onClaimSuccess) {
            onClaimSuccess();
          }
        },
        onError: (error) => {
          // Handle specific error cases
          let errorMessage;
          if (error.response?.status === 404) {
            errorMessage = t(
              "common.invalid_claim_code",
              "Invalid claim code or device not found"
            );
          } else if (error.response?.status === 409) {
            errorMessage = t(
              "common.device_already_claimed",
              "Device has already been claimed"
            );
          } else {
            errorMessage = t("common.error_claiming_device", {
              error: error.response?.data?.detail || error.message,
            });
          }

          UtilityService.updateSnackbarMessage(errorMessage, true);
        },
      }
    );
  };
  return {
    // Form state
    claimCode,
    setClaimCode,
    organisationID,
    setOrganisationID,
    organisations,

    // Actions
    claimDevice,
    resetForm,

    // Loading state
    isClaimingDevice: claimDeviceMutation.isPending,
  };
};
